import React, { useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { motion, useInView } from 'framer-motion';
import {
  FaPhoneAlt,
  FaDrawPolygon,
  FaWrench,
  FaTools,
  FaTasks,
  FaRoad,
  FaBuilding,
  FaShieldAlt,
  FaHardHat,
  FaLightbulb,
  FaClock,
  FaCity
} from 'react-icons/fa';
import constrHero from '../assets/constrHero.jpg';
import whyChooseUsImg from '../assets/constr.jpg';

const services = [
  {
    icon: <FaCity />,
    title: "Residential & Commercial Buildings",
    desc: "Design, construction, and finishing of durable, future-ready spaces."
  },
  {
    icon: <FaRoad />,
    title: "Roads & Pavements",
    desc: "Durable, high-quality surfaces for safe and efficient transportation."
  },
  {
    icon: <FaDrawPolygon />,
    title: "Structural Engineering",
    desc: "Bridges, foundations, and load-bearing structures built to last."
  },
  {
    icon: <FaWrench />,
    title: "Renovation & Remodeling",
    desc: "Upgrading spaces for modern functionality and improved aesthetics."
  },
  {
    icon: <FaTools />,
    title: "Site Preparation & Earthworks",
    desc: "Clearing, grading, and foundation works for strong project starts."
  },
  {
    icon: <FaTasks />,
    title: "Project Management",
    desc: "Delivering complex projects on time, within scope, and budget."
  },
];

const setApart = [
  {
    icon: <FaBuilding />,
    title: "Proven Expertise",
    desc: "Years of experience delivering projects of all sizes."
  },
  {
    icon: <FaShieldAlt />,
    title: "Quality Assurance",
    desc: "Strict adherence to building codes and best practices."
  },
  {
    icon: <FaHardHat />,
    title: "Skilled Workforce",
    desc: "Engineers, architects, and technicians with unmatched skills."
  },
  {
    icon: <FaLightbulb />,
    title: "Innovative Solutions",
    desc: "Modern techniques and sustainable construction practices."
  },
  {
    icon: <FaClock />,
    title: "On-Time Delivery",
    desc: "We respect your deadlines and keep your project on track."
  }
];

// ✅ Scroll Animation Hook
const useScrollFadeIn = (delay = 0) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return {
    ref,
    initial: { opacity: 0, y: 40 },
    animate: inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 },
    transition: { duration: 0.8, ease: 'easeOut', delay },
    viewport: { once: true }
  };
};

function Construction() {
  const titleAnim = useScrollFadeIn();
  const subtitleAnim = useScrollFadeIn();
  const buttonsAnim = useScrollFadeIn();
  const contactAnim = useScrollFadeIn();

  const navigate = useNavigate();

  return (
    <div>
      {/* ✅ Hero Section */}
      <section
        className="relative w-full h-[40vh] bg-cover bg-center flex items-center justify-center text-white"
        style={{ backgroundImage: `url(${constrHero})` }}
      >
        <div className="absolute inset-0 bg-black/60 z-10" />
        <div className="relative z-20 max-w-5xl mx-auto px-6  space-y-6 items-center text-center ">
          <motion.h1 {...titleAnim} className="text-[32px]  font-bold">
            Building Strong Foundations for a Better Future
          </motion.h1>
          <motion.div {...contactAnim} className="mt-6 flex justify-center items-center gap-2 text-sm text-white/80">
            <FaPhoneAlt />
            <span>Need urgent help? Call us: +255 714 183 424</span>
          </motion.div>
        </div>
      </section>

      
      <section className="w-full py-16 px-4 sm:px-10 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center relative">

          {/* Text Section */}
          <div className="bg-gray-950 text-white p-12 md:p-10 rounded-lg relative z-10 w-full md:w-[60%] shadow-lg mt-6 md:mt-0 md:-mr-20">
            <p className="text-sm text-orange-600 mb-2 uppercase">Our Expertise</p>
            <h2 className="text-2xl md:text-3xl font-bold mb-4 mr-10">
              Comprehensive  Civil Engineering Services
            </h2>
            <p className="text-gray-300 text-sm md:text-base mb-6 mr-10">
              We provide end-to-end construction and civil engineering solutions tailored to your needs. Whether it’s building from the ground up, upgrading existing infrastructure, or delivering specialized projects, our team ensures quality, safety, and on-time delivery.
            </p>
          </div>

          {/* Image Section */}
          <div className="relative z-20 w-full md:w-1/2">
            <img
              src={whyChooseUsImg}
              alt="Our Construction Work"
              className="w-full h-auto object-cover rounded-lg shadow-lg md:mt-0 mt-6"
            />
          </div>

        </div>
      </section>


      {/* ✅ Services Grid */}
      <section className="text-white py-16 px-6 sm:px-12 bg-gray-900 rounded-lg">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          {services.map((service, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center bg-gray-800 rounded-lg p-6 shadow-lg"
            >
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-orange-500 text-white text-2xl mb-4">
                {service.icon}
              </div>
              <h4 className="font-semibold text-xl md:text-2xl border-b-4 border-white w-1/2 pb-1 mb-3">
                {service.title}
              </h4>
              <p className="text-sm text-gray-300 md:text-lg lg:text-xl">
                {service.desc}
              </p>
            </div>
          ))}
        </div>
      </section>


      {/* ✅ Why Choose Us Section */}
      <section className="w-full py-16 px-6 md:px-24 bg-white">
        <div className="flex flex-col items-center gap-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 leading-snug">
            Why Choose Us for Your Construction Projects
          </h2>

          {/* Cards Container */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 w-full max-w-6xl">
            {setApart.map((item, index) => (
              <div
                key={index}
                className="relative border border-gray-300 rounded-lg p-6 pt-12 text-center bg-white shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                {/* Icon Circle */}
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl shadow-md">
                  {item.icon}
                </div>

                {/* Title */}
                <h3 className="font-semibold text-xl md:text-2xl borde mb-2">{item.title}</h3>

                {/* Description */}
                <p className="text-gray-600 text-sm md:text-lg lg:text-xl">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ Final Call to Action */}
      <section className="w-full py-16 px-6 bg-gradient-to-r from-gray-950 to-gray-900 text-center text-white mb-36">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl  leading-snug font-bold mb-4">Let’s Build Something Great Together</h2>
          <p className="text-lg mb-6">No matter the size or complexity of your project, we are ready to deliver reliable, high-quality, and cost-effective construction services.</p>
          <button onClick={() => navigate("/contact")} className="bg-orange-400 text-black font-semibold py-3 px-6 rounded-xl transition duration-300">
            Contact Us Now
          </button>
        </motion.div>
      </section>
    </div>
  );
}

export default Construction;
